<template>
  <v-container fluid class="pa-4">
    <!-- <PageHeader
      icon="mdi-lan-connect"
      title="Status Sambungan"
      subtitle="Pemantauan sambungan MySQL / ClickHouse, sync manual dan berjadual, serta sejarah job"
    /> -->

    <!-- Connection status -->
    <!-- Susun atur dua lajur: KIRI = keadaan & tetapan (Status, Jadual
         Auto-Sync, Prestasi), KANAN = tindakan & keluaran (Sync manual + panel
         log, Sejarah Sync).

         Nisbah 5/7, bukan 6/6: panel log dan jadual sejarah 7 lajur itulah yang
         kehabisan ruang mendatar, bukan kad status yang hanya senarai label.

         SATU <v-row>, bukan dua baris bertindih seperti sebelum ini. Dengan dua
         baris, kad kanan bawah tidak boleh naik mengisi ruang yang ditinggalkan
         kad kiri yang lebih pendek — baris kedua sentiasa bermula di bawah kad
         TERTINGGI baris pertama, jadi terbentuk jurang putih di bawah Status.

         Setiap lajur `cols="12"` supaya pada telefon ia bertindan penuh —
         dahulu dua lajur bawah ditulis `cols="6"` tanpa titik putus, jadi
         jadual sejarah kekal separuh skrin walaupun pada skrin paling kecil. -->
    <v-row>
      <v-col cols="12" md="5">
        <v-card outlined class="mb-4">
          <v-card-title class="py-3 kad__tajuk">
            <v-icon small class="mr-2">mdi-database-check</v-icon>
            <span class="subtitle-1 font-weight-medium">Status Sambungan Pangkalan Data</span>
          </v-card-title>
          <v-card-text class="pt-5">
            <v-alert
              v-if="mysql && mysql.status"
              :type="mysql.status === 'Connected' ? 'success' : 'error'"
              border="left"
              elevation="2"
              class="mb-4"
            >
              <div class="d-flex align-center justify-space-between">
                <strong>MySQL:</strong> <span>{{ mysql.status }}</span>
              </div>
              <div class="mt-2 text-caption">
                <div><strong>Host:</strong> {{ mysql.host }}</div>
                <div><strong>Port:</strong> {{ mysql.port }}</div>
                <div><strong>Database:</strong> {{ mysql.database }}</div>
              </div>
            </v-alert>

            <v-alert
              v-if="clickhouse && clickhouse.status"
              :type="clickhouse.status === 'Connected' ? 'success' : 'error'"
              border="left"
              elevation="2"
            >
              <div class="d-flex align-center justify-space-between">
                <strong>ClickHouse:</strong> <span>{{ clickhouse.status }}</span>
              </div>
              <div class="mt-2 text-caption">
                <div><strong>Host:</strong> {{ clickhouse.host }}</div>
                <div><strong>Database:</strong> {{ clickhouse.database }}</div>
              </div>
            </v-alert>

            <div class="text-center mt-4">
              <v-btn class="btn-teal" @click="fetchStatus" :loading="loading">
                Refresh Status
              </v-btn>
            </div>

            <div class="text-center text-caption grey--text mt-4">
              Versi binaan: {{ buildVersion }}
            </div>
          </v-card-text>
        </v-card>

        <v-card outlined class="mb-4">
          <v-card-title class="py-3 kad__tajuk">
            <v-icon small class="mr-2">mdi-calendar-clock</v-icon>
            <span class="subtitle-1 font-weight-medium">Jadual Auto-Sync ClickHouse</span>
          </v-card-title>
          <v-card-text class="pt-4">
            <div v-if="scheduleLoading" class="text-center py-4">
              <v-progress-circular indeterminate color="#037769"></v-progress-circular>
            </div>
            <template v-else>
              <v-row dense class="mb-2">
                <v-col cols="12" sm="4">
                  <v-select :items="modeOptions" v-model="scheduleMode" label="Mod" dense outlined hide-details />
                </v-col>
                
                <v-col cols="12" sm="4" v-if="scheduleMode === 'Daily' || scheduleMode === 'Weekly'">
                  <v-text-field type="time" v-model="scheduleTime" label="Masa" dense outlined hide-details />
                </v-col>

                <v-col cols="12" sm="4" v-if="scheduleMode === 'Advanced'">
                  <v-text-field v-model="scheduleCron" label="Cron Expr" dense outlined hide-details />
                </v-col>
                
                <v-col cols="12" sm="4" v-if="scheduleMode !== 'Disabled'">
                  <v-combobox :items="timezoneOptions" v-model="scheduleTimezone" label="Zon Masa" dense outlined hide-details />
                </v-col>
              </v-row>

              <v-row dense v-if="scheduleMode === 'Weekly'" class="mb-2">
                <v-col cols="12">
                  <v-select :items="dayOptions" v-model="scheduleDays" multiple chips small-chips label="Hari" dense outlined hide-details />
                </v-col>
              </v-row>

              <v-alert v-if="scheduleError" type="error" dense class="mb-2 text-caption">
                {{ scheduleError }}
              </v-alert>

              <div class="d-flex align-center mt-3">
                <div class="d-flex align-center">
                   <v-chip :color="scheduleActiveState ? 'success' : 'grey'" small text-color="white" class="mr-3">
                     {{ scheduleActiveState ? 'Aktif' : 'Tidak Aktif' }}
                   </v-chip>
                   <span class="text-caption font-weight-medium" v-if="scheduleActiveState && scheduleData && scheduleData.nextRun">
                     Seterusnya: {{ formattedNextRun }}
                   </span>
                </div>
                <v-spacer />
                <v-btn class="btn-teal" small :loading="scheduleSaving" :disabled="!isScheduleValid" @click="saveSchedule">
                  Simpan Jadual
                </v-btn>
              </div>
              <div class="text-caption grey--text mt-2" v-if="scheduleData && scheduleData.schedule && scheduleData.schedule.last_updated_at">
                Kemas kini terakhir oleh {{ scheduleData.schedule.last_updated_by_name || 'sistem' }}, {{ timeAgo(scheduleData.schedule.last_updated_at) }}
              </div>
            </template>
          </v-card-text>
        </v-card>
        
        <v-card outlined>
          <v-card-title class="py-3 kad__tajuk">
            <v-icon small class="mr-2">mdi-speedometer</v-icon>
            <span class="subtitle-1 font-weight-medium">Prestasi Sync ClickHouse</span>
          </v-card-title>
          <v-card-text class="pt-4">
            <div v-if="syncConfigsLoading" class="text-center py-4">
              <v-progress-circular indeterminate color="#037769"></v-progress-circular>
            </div>
            <template v-else>
              <v-row dense v-for="cfg in syncConfigs" :key="cfg.id" class="align-center mb-2">
                <v-col cols="8">
                  <v-text-field
                    v-model="cfg.editValue"
                    :label="cfg.name"
                    dense
                    outlined
                    hide-details
                  ></v-text-field>
                </v-col>
                <v-col cols="4">
                  <v-btn
                    class="btn-teal"
                    small
                    :loading="cfg.saving"
                    @click="saveSyncConfig(cfg)"
                    :disabled="cfg.editValue === cfg.value"
                  >
                    Simpan
                  </v-btn>
                </v-col>
              </v-row>
              <div v-if="syncConfigs.length === 0" class="text-caption grey--text">
                Tiada konfigurasi sync dijumpai.
              </div>
            </template>
          </v-card-text>
        </v-card>
      </v-col>

      <v-col cols="12" md="7">
        <v-card outlined class="mb-4">
          <v-card-title class="py-3 kad__tajuk">
            <v-icon small class="mr-2">mdi-database-sync</v-icon>
            <span class="subtitle-1 font-weight-medium">Manual Sync MySQL ke ClickHouse</span>
          </v-card-title>
          <v-card-text class="pt-5">
            <v-alert
              v-if="runningBanner"
              type="warning"
              border="left"
              dense
              class="mb-3"
            >
              {{ runningBanner }}
            </v-alert>

            <div class="d-flex align-center mb-3">
              <v-btn
                class="btn-teal"
                :disabled="syncDisabled"
                :loading="syncStarting"
                @click="startSync"
              >
                <v-icon left>mdi-database-sync</v-icon>
                Mulakan Sync
              </v-btn>
              <v-spacer />
              <v-chip
                v-if="streamStatus"
                :color="streamStatusColor"
                small
                text-color="white"
              >
                {{ streamStatus }}
              </v-chip>
            </div>

            <div v-if="totalTables > 0" class="mb-2">
              <div class="d-flex justify-space-between text-caption">
                <span>{{ doneTables }} / {{ totalTables }} jadual</span>
                <span>{{ progressPct }}%</span>
              </div>
              <v-progress-linear
                :value="progressPct"
                :indeterminate="streamOpen && totalTables === 0"
                height="8"
                rounded
                color="#037769"
              />
            </div>

            <v-card outlined class="mt-2">
              <div class="log-panel" ref="logPanel">
                <div
                  v-for="(line, i) in logLines"
                  :key="i"
                  :class="['log-line', `log-${line.kind}`]"
                >
                  <span class="log-ts">{{ line.ts }}</span>
                  <span class="log-msg">{{ line.msg }}</span>
                </div>
                <div v-if="streamOpen" class="log-line log-info">
                  <v-progress-circular
                    indeterminate
                    size="12"
                    width="2"
                    color="#037769"
                  />
                  <span class="ml-2">Menyambung ke stream...</span>
                </div>
                <div
                  v-if="logLines.length === 0 && !streamOpen"
                  class="log-empty"
                >
                  Belum ada log. Tekan "Mulakan Sync" untuk memulakan.
                </div>
              </div>
            </v-card>
          </v-card-text>
        </v-card>

        <v-card outlined>
          <v-card-title class="py-3 kad__tajuk">
            <v-icon small class="mr-2">mdi-history</v-icon>
            <span class="subtitle-1 font-weight-medium">Sejarah Sync</span>
            <v-spacer />
            <v-btn icon @click="fetchHistory" :loading="historyLoading">
              <v-icon>mdi-refresh</v-icon>
            </v-btn>
          </v-card-title>

          <v-card-text v-if="stuckJob" class="pb-0 pt-4">
            <v-alert type="warning" border="left" dense class="mb-0">
              <v-row align="center" no-gutters>
                <v-col>
                  <strong>⚠ Job #{{ stuckJob.id }}</strong> telah berjalan selama {{ stuckJob.stuckMinutes }} minit.
                </v-col>
                <v-col class="text-right" cols="auto">
                  <v-btn outlined color="black" small @click="confirmUnlock(stuckJob)">Force Unlock</v-btn>
                </v-col>
              </v-row>
            </v-alert>
          </v-card-text>

          <v-data-table
            :headers="historyHeaders"
            :items="history"
            :loading="historyLoading"
            :items-per-page="10"
            class="elevation-0"
            @click:row="openJob"
          >
            <template #item.status="{ item }">
              <v-chip
                :color="statusColor(item.status)"
                small
                text-color="white"
              >
                {{ item.status }}
              </v-chip>
            </template>
            <template #item.started_at="{ item }">
              {{ formatDate(item.started_at) }}
            </template>
            <template #item.duration_seconds="{ item }">
              {{ formatDuration(item.duration_seconds) }}
            </template>
            <template #item.total_rows="{ item }">
              {{ formatNumber(item.total_rows) }}
            </template>
            <template #item.triggered_by_name="{ item }">
              {{ item.triggered_by_name || '-' }}
              <v-chip v-if="item.triggered_by_id === 0" x-small color="info" class="ml-1">auto</v-chip>
            </template>
          </v-data-table>
        </v-card>
      </v-col>
    </v-row>

    <!-- Unlock modal -->
    <v-dialog v-model="unlockDialog" max-width="500">
      <v-card>
        <v-card-title class="py-3 kad__tajuk">
          <v-icon small class="mr-2">mdi-lock-open-alert</v-icon>
          <span class="subtitle-1 font-weight-medium">Sahkan Force Unlock</span>
        </v-card-title>
        <v-card-text>
          Paksa lepaskan kunci dari Job #{{ jobToUnlock?.id }} (dimulakan oleh {{ jobToUnlock?.triggered_by_name || 'pengguna' }}, {{ jobToUnlock?.stuckMinutes }} minit yang lalu)?
          <br><br>
          Tindakan ini tidak membatalkan proses sync yang sedang berjalan, tetapi membenarkan jadual baharu untuk bermula sekiranya proses lama telah tersangkut.
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn text @click="unlockDialog = false">Batal</v-btn>
          <v-btn color="error" :loading="unlocking" @click="doUnlock">Force Unlock</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Job detail modal -->
    <v-dialog v-model="jobDialog" max-width="900">
      <v-card v-if="jobDetail">
        <v-card-title class="py-3 kad__tajuk">
          <v-icon small class="mr-2">mdi-file-compare</v-icon>
          <span class="subtitle-1 font-weight-medium">Job #{{ jobDetail.id }}</span>
          <v-spacer />
          <v-btn icon @click="jobDialog = false">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-card-title>
        <v-card-text class="pt-4">
          <v-row dense>
            <v-col cols="12" sm="6" md="4">
              <div class="text-caption grey--text">Status</div>
              <v-chip
                :color="statusColor(jobDetail.status)"
                small
                text-color="white"
              >
                {{ jobDetail.status }}
              </v-chip>
            </v-col>
            <v-col cols="12" sm="6" md="4">
              <div class="text-caption grey--text">Dimulakan oleh</div>
              <div>{{ jobDetail.triggered_by_name || '-' }}</div>
            </v-col>
            <v-col cols="12" sm="6" md="4">
              <div class="text-caption grey--text">Tempoh</div>
              <div>{{ formatDuration(jobDetail.duration_seconds) }}</div>
            </v-col>
            <v-col cols="12" sm="6" md="4">
              <div class="text-caption grey--text">Mula</div>
              <div>{{ formatDate(jobDetail.started_at) }}</div>
            </v-col>
            <v-col cols="12" sm="6" md="4">
              <div class="text-caption grey--text">Tamat</div>
              <div>{{ formatDate(jobDetail.finished_at) }}</div>
            </v-col>
            <v-col cols="12" sm="6" md="4">
              <div class="text-caption grey--text">Jadual / Baris</div>
              <div>
                {{ jobDetail.tables_synced || 0 }} /
                {{ formatNumber(jobDetail.total_rows) }}
              </div>
            </v-col>
          </v-row>

          <v-alert
            v-if="jobDetail.error_message"
            type="error"
            dense
            class="mt-3"
          >
            {{ jobDetail.error_message }}
          </v-alert>

          <div class="text-caption grey--text mt-4 mb-1">Log penuh</div>
          <pre class="log-full">{{ jobDetail.log || '(tiada log)' }}</pre>
        </v-card-text>
      </v-card>
    </v-dialog>
  </v-container>
</template>

<script>
import axios from '@/plugins/axios'

export default {
  name: 'ConnectionStatus',
  data() {
    return {
      mysql: null,
      clickhouse: null,
      loading: false,

      // sync state
      syncStarting: false,
      streamOpen: false,
      streamStatus: '',
      currentJobId: null,
      runningBanner: '',
      totalTables: 0,
      doneTables: 0,
      logLines: [],
      abortCtrl: null,

      // history
      history: [],
      historyLoading: false,
      historyHeaders: [
        { text: 'ID', value: 'id', width: 70 },
        { text: 'Status', value: 'status', width: 110 },
        { text: 'Oleh', value: 'triggered_by_name' },
        { text: 'Mula', value: 'started_at' },
        { text: 'Tempoh', value: 'duration_seconds' },
        { text: 'Jadual', value: 'tables_synced' },
        { text: 'Baris', value: 'total_rows' },
      ],

      // job detail
      jobDialog: false,
      jobDetail: null,

      // sync configs
      syncConfigs: [],
      syncConfigsLoading: false,

      // schedule data
      scheduleData: null,
      scheduleMode: 'Disabled',
      scheduleTime: '',
      scheduleDays: [],
      scheduleCron: '',
      scheduleTimezone: 'Asia/Kuala_Lumpur',
      scheduleLoading: false,
      scheduleSaving: false,
      scheduleError: null,
      modeOptions: ['Disabled', 'Daily', 'Weekly', 'Advanced'],
      timezoneOptions: ['Asia/Kuala_Lumpur', 'Asia/Singapore', 'Asia/Jakarta'],
      dayOptions: [
        { text: 'Ahad', value: 0 },
        { text: 'Isnin', value: 1 },
        { text: 'Selasa', value: 2 },
        { text: 'Rabu', value: 3 },
        { text: 'Khamis', value: 4 },
        { text: 'Jumaat', value: 5 },
        { text: 'Sabtu', value: 6 },
      ],

      // unlock data
      unlockDialog: false,
      jobToUnlock: null,
      unlocking: false,
      unlockedJobIds: [],
    }
  },
  computed: {
    scheduleActiveState() {
      return this.scheduleData && this.scheduleData.schedulerActive
    },
    isScheduleValid() {
      if (this.scheduleMode === 'Disabled') return true
      if (!this.scheduleTimezone) return false
      if (this.scheduleMode === 'Daily') return !!this.scheduleTime
      if (this.scheduleMode === 'Weekly') return !!this.scheduleTime && this.scheduleDays.length > 0
      if (this.scheduleMode === 'Advanced') return !!this.scheduleCron
      return false
    },
    computedCronExpression() {
      if (this.scheduleMode === 'Daily') {
        const [h, m] = (this.scheduleTime || '00:00').split(':')
        return `${parseInt(m, 10)} ${parseInt(h, 10)} * * *`
      }
      if (this.scheduleMode === 'Weekly') {
        const [h, m] = (this.scheduleTime || '00:00').split(':')
        const dow = [...this.scheduleDays].sort().join(',') || '*'
        return `${parseInt(m, 10)} ${parseInt(h, 10)} * * ${dow}`
      }
      return this.scheduleCron
    },
    formattedNextRun() {
      if (!this.scheduleData || !this.scheduleData.nextRun) return '-'
      const d = new Date(this.scheduleData.nextRun)
      if (isNaN(d.getTime())) return '-'
      const dFormat = d.toLocaleDateString('en-GB', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' })
      const tFormat = d.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' })
      let tz = ''
      try {
        const parts = new Intl.DateTimeFormat('en-GB', { timeZoneName: 'short' }).formatToParts(d)
        const tzPart = parts.find(p => p.type === 'timeZoneName')
        if (tzPart) tz = ' ' + tzPart.value
      } catch(e){
        // ignore format error
      }
      return `${dFormat}, ${tFormat}${tz}`
    },
    stuckJob() {
      const runningJobs = this.history.filter(j => j.status === 'running' && !this.unlockedJobIds.includes(j.id))
      for (const job of runningJobs) {
        const d = new Date(job.started_at)
        if (!isNaN(d.getTime())) {
          const diffMin = (new Date() - d) / 60000
          if (diffMin > 30) {
            return { ...job, stuckMinutes: Math.floor(diffMin) }
          }
        }
      }
      return null
    },
    buildVersion() {
      return process.env.VUE_APP_BUILD_VERSION || 'unknown'
    },
    apiUrl() {
      return process.env.VUE_APP_API_URL
    },
    token() {
      return window.localStorage.getItem('access_token') || ''
    },
    syncDisabled() {
      return this.streamOpen || this.syncStarting || !!this.runningBanner
    },
    progressPct() {
      if (!this.totalTables) return 0
      return Math.min(100, Math.round((this.doneTables / this.totalTables) * 100))
    },
    streamStatusColor() {
      if (this.streamStatus === 'Selesai') return 'success'
      if (this.streamStatus === 'Gagal') return 'error'
      return 'primary'
    },
  },
  mounted() {
    this.fetchStatus()
    this.fetchHistory()
    this.fetchSchedule()
    this.fetchSyncConfigs()
  },
  beforeDestroy() {
    if (this.abortCtrl) this.abortCtrl.abort()
  },
  methods: {
    // ----- sync configs -----
    async fetchSyncConfigs() {
      this.syncConfigsLoading = true
      try {
        const res = await axios.get(`${this.apiUrl}/admin/config?type=sync_clickhouse`)
        this.syncConfigs = (res.data.config || []).map(c => ({
          ...c,
          editValue: c.value,
          saving: false
        }))
      } catch (err) {
        console.error('Error fetching sync configs:', err)
      } finally {
        this.syncConfigsLoading = false
      }
    },
    async saveSyncConfig(cfg) {
      cfg.saving = true
      try {
        const res = await axios.put(`${this.apiUrl}/admin/config/${cfg.id}`, { value: cfg.editValue })
        cfg.value = res.data.config.value
        cfg.editValue = cfg.value
      } catch (err) {
        console.error('Error saving sync config:', err)
        alert(err.response?.data?.message || 'Gagal menyimpan konfigurasi')
      } finally {
        cfg.saving = false
      }
    },

    // ----- schedule -----
    async fetchSchedule() {
      this.scheduleLoading = true
      this.scheduleError = null
      try {
        const res = await axios.get(`${this.apiUrl}/admin/sync-clickhouse/schedule`)
        this.scheduleData = res.data
        const sched = res.data.schedule || {}
        this.scheduleTimezone = sched.timezone || 'Asia/Kuala_Lumpur'
        this.parseCron(sched.cron_expression || '30 2 * * *', sched.enabled)
      } catch (error) {
        console.error('Error fetching schedule:', error)
        this.scheduleError = 'Gagal memuat turun jadual'
      } finally {
        this.scheduleLoading = false
      }
    },
    parseCron(cronExpr, isEnabled) {
      if (!isEnabled) {
        this.scheduleMode = 'Disabled'
      }
      const parts = (cronExpr || '').trim().split(/\s+/)
      if (parts.length === 5) {
        const [min, hour, dom, mon, dow] = parts
        
        if (/^\d+$/.test(min) && /^\d+$/.test(hour) && dom === '*' && mon === '*') {
          if (dow === '*') {
            if (isEnabled) this.scheduleMode = 'Daily'
            this.scheduleTime = `${hour.padStart(2, '0')}:${min.padStart(2, '0')}`
            this.scheduleCron = cronExpr
            return
          } else if (/^[0-6](,[0-6])*$/.test(dow)) {
            if (isEnabled) this.scheduleMode = 'Weekly'
            this.scheduleTime = `${hour.padStart(2, '0')}:${min.padStart(2, '0')}`
            this.scheduleDays = dow.split(',').map(Number)
            this.scheduleCron = cronExpr
            return
          }
        }
      }
      
      if (isEnabled) this.scheduleMode = 'Advanced'
      this.scheduleCron = cronExpr
    },
    async saveSchedule() {
      this.scheduleSaving = true
      this.scheduleError = null
      const payload = {}
      if (this.scheduleMode === 'Disabled') {
        payload.enabled = false
      } else {
        payload.enabled = true
        payload.cronExpression = this.computedCronExpression
        payload.timezone = this.scheduleTimezone
      }

      try {
        const res = await axios.put(`${this.apiUrl}/admin/sync-clickhouse/schedule`, payload)
        this.scheduleData = res.data
        const sched = res.data.schedule || {}
        this.scheduleTimezone = sched.timezone || 'Asia/Kuala_Lumpur'
        this.parseCron(sched.cron_expression || '30 2 * * *', sched.enabled)
      } catch (err) {
        console.error('Error saving schedule', err)
        this.scheduleError = err.response?.data?.message || 'Gagal menyimpan jadual'
      } finally {
        this.scheduleSaving = false
      }
    },

    // ----- unlock -----
    confirmUnlock(job) {
      this.jobToUnlock = job
      this.unlockDialog = true
    },
    async doUnlock() {
      this.unlocking = true
      try {
        const res = await axios.post(`${this.apiUrl}/admin/sync-clickhouse/unlock`)
        if (res.data.released || res.data.message === 'No running job to unlock') {
          this.unlockDialog = false
          if (this.jobToUnlock) {
            this.unlockedJobIds.push(this.jobToUnlock.id)
          }
          this.fetchHistory()
        }
      } catch (err) {
        console.error('Failed to unlock', err)
      } finally {
        this.unlocking = false
      }
    },

    // ----- connection status -----
    async fetchStatus() {
      this.loading = true
      try {
        const response = await axios.get(`${this.apiUrl}/db-status/connection`)
        this.mysql = response.data.data.mysql
        this.clickhouse = response.data.data.clickhouse
      } catch (error) {
        console.error('Error fetching status:', error)
        this.mysql = { status: 'Failed to fetch status' }
        this.clickhouse = { status: 'Failed to fetch status' }
      } finally {
        this.loading = false
      }
    },

    // ----- history -----
    async fetchHistory() {
      this.historyLoading = true
      try {
        const res = await axios.get(`${this.apiUrl}/admin/sync-clickhouse/history`)
        const items = Array.isArray(res.data) ? res.data : (res.data.jobs || res.data.data || [])
        this.history = items
        const running = items.find((j) => j.status === 'running')
        if (running) {
          this.runningBanner = `Sync sedang berjalan, dimulakan oleh ${
            running.triggered_by_name || 'pengguna'
          } pada ${this.formatTime(running.started_at)}.`
        } else {
          this.runningBanner = ''
        }
      } catch (err) {
        console.error('Error fetching history:', err)
      } finally {
        this.historyLoading = false
      }
    },

    async openJob(item) {
      try {
        const res = await axios.get(
          `${this.apiUrl}/admin/sync-clickhouse/${item.id}`,
        )
        this.jobDetail = res.data.job || res.data.data || res.data
        this.jobDialog = true
      } catch (err) {
        console.error('Error fetching job:', err)
      }
    },

    // ----- sync trigger + SSE -----
    async startSync() {
      this.resetSyncState()
      this.syncStarting = true
      this.appendLog('info', 'Menghantar permintaan sync...')

      this.abortCtrl = new AbortController()
      let response
      try {
        response = await fetch(`${this.apiUrl}/admin/sync-clickhouse`, {
          method: 'GET',
          headers: {
            Authorization: `Bearer ${this.token}`,
            Accept: 'text/event-stream',
          },
          signal: this.abortCtrl.signal,
        })
      } catch (err) {
        this.syncStarting = false
        this.appendLog('error', `Gagal menyambung: ${err.message}`)
        return
      }

      if (response.status === 409) {
        let body = {}
        try {
          body = await response.json()
        } catch (e) { /* ignore */ }
        const job = body.job || {}
        const who = job.triggeredBy || 'pengguna lain'
        const when = job.startedAt ? this.formatTime(job.startedAt) : ''
        this.runningBanner = `Sync sudah berjalan, dimulakan oleh ${who}${
          when ? ' pada ' + when : ''
        }. Sila cuba lagi selepas selesai.`
        this.appendLog('warn', this.runningBanner)
        this.syncStarting = false
        this.fetchHistory()
        return
      }

      if (!response.ok) {
        this.syncStarting = false
        const msg =
          response.status === 403
            ? 'Akses ditolak (perlu peranan ADMIN).'
            : `HTTP ${response.status}`
        this.appendLog('error', msg)
        return
      }

      this.syncStarting = false
      this.streamOpen = true
      this.streamStatus = 'Berjalan'

      try {
        await this.consumeSseStream(response)
      } catch (err) {
        if (err.name !== 'AbortError') {
          this.appendLog('error', `Stream ralat: ${err.message}`)
          this.streamStatus = 'Gagal'
        }
      } finally {
        this.streamOpen = false
        this.abortCtrl = null
        this.fetchHistory()
      }
    },

    async consumeSseStream(response) {
      const reader = response.body.getReader()
      const decoder = new TextDecoder('utf-8')
      let buf = ''

      let streaming = true
      while (streaming) {
        const { done, value } = await reader.read()
        if (done) {
          streaming = false
          break
        }
        buf += decoder.decode(value, { stream: true })

        let sepIdx = buf.indexOf('\n\n')
        while (sepIdx !== -1) {
          const raw = buf.slice(0, sepIdx)
          buf = buf.slice(sepIdx + 2)
          this.handleSseBlock(raw)
          sepIdx = buf.indexOf('\n\n')
        }
      }
    },

    handleSseBlock(raw) {
      const lines = raw.split('\n')
      let event = 'message'
      let data = ''
      for (const line of lines) {
        if (!line || line.startsWith(':')) continue // heartbeat / comment
        if (line.startsWith('event:')) {
          event = line.slice(6).trim()
        } else if (line.startsWith('data:')) {
          data += (data ? '\n' : '') + line.slice(5).trim()
        }
      }
      if (!data && event === 'message') return

      let payload = null
      if (data) {
        try {
          payload = JSON.parse(data)
        } catch (e) {
          payload = { raw: data }
        }
      }
      this.handleSseEvent(event, payload || {})
    },

    handleSseEvent(event, d) {
      switch (event) {
        case 'job':
          this.currentJobId = d.jobId
          this.appendLog(
            'info',
            `Job #${d.jobId} dimulakan oleh ${d.triggeredBy || 'anda'}.`,
          )
          break
        case 'start':
          this.totalTables = (d.tables || []).length
          this.doneTables = 0
          this.appendLog(
            'info',
            `Mula sync ${d.dbName} → ${d.chDatabase} (${this.totalTables} jadual).`,
          )
          break
        case 'table':
          this.doneTables += 1
          this.appendLog(
            'ok',
            `[${d.table}] ${this.formatNumber(d.rows)} baris dalam ${
              d.durationSec
            }s`,
          )
          break
        case 'skip':
          this.doneTables += 1
          this.appendLog(
            'warn',
            `[${d.table}] dilangkau — ${d.reason || 'tidak diketahui'}`,
          )
          break
        case 'done':
          this.streamStatus = 'Selesai'
          this.appendLog(
            'done',
            `Selesai: ${d.tablesSynced} jadual, ${this.formatNumber(
              d.totalRows,
            )} baris dalam ${d.durationSec}s.`,
          )
          break
        case 'error':
          this.streamStatus = 'Gagal'
          this.appendLog('error', `Ralat: ${d.message || 'tidak diketahui'}`)
          break
        default:
          if (d && Object.keys(d).length) {
            this.appendLog('info', `[${event}] ${JSON.stringify(d)}`)
          }
      }
    },

    resetSyncState() {
      this.logLines = []
      this.totalTables = 0
      this.doneTables = 0
      this.streamStatus = ''
      this.currentJobId = null
    },

    appendLog(kind, msg) {
      this.logLines.push({ kind, msg, ts: this.nowHms() })
      this.$nextTick(() => {
        const el = this.$refs.logPanel
        if (el) el.scrollTop = el.scrollHeight
      })
    },

    // ----- formatting -----
    timeAgo(dateStr) {
      if (!dateStr) return ''
      const d = new Date(dateStr)
      if (isNaN(d.getTime())) return ''
      const diffSecs = Math.floor((new Date() - d) / 1000)
      if (diffSecs < 60) return `${diffSecs} saat yang lalu`
      if (diffSecs < 3600) return `${Math.floor(diffSecs / 60)} minit yang lalu`
      if (diffSecs < 86400) return `${Math.floor(diffSecs / 3600)} jam yang lalu`
      return `${Math.floor(diffSecs / 86400)} hari yang lalu`
    },
    nowHms() {
      const d = new Date()
      return [d.getHours(), d.getMinutes(), d.getSeconds()]
        .map((n) => String(n).padStart(2, '0'))
        .join(':')
    },
    formatDate(v) {
      if (!v) return '-'
      const d = new Date(v)
      if (isNaN(d.getTime())) return v
      const pad = (n) => String(n).padStart(2, '0')
      return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(
        d.getDate(),
      )} ${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())}`
    },
    formatTime(v) {
      if (!v) return '-'
      const d = new Date(v)
      if (isNaN(d.getTime())) return v
      const pad = (n) => String(n).padStart(2, '0')
      return `${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())}`
    },
    formatDuration(sec) {
      if (sec == null) return '-'
      const s = Number(sec)
      if (!isFinite(s)) return '-'
      if (s < 60) return `${s.toFixed(1)}s`
      const m = Math.floor(s / 60)
      const r = Math.round(s - m * 60)
      return `${m}m ${r}s`
    },
    formatNumber(n) {
      if (n == null) return '-'
      return Number(n).toLocaleString('en-US')
    },
    statusColor(status) {
      if (status === 'completed') return 'success'
      if (status === 'running') return 'primary'
      if (status === 'failed') return 'error'
      return 'grey'
    },
  },
}
</script>

<style scoped>
/* Kepala kad — corak yang sama seperti Jadual Awalan / Julat Harga / Log Audit:
   jalur putih dengan ikon + tajuk, bukan blok warna. Halaman ini dahulunya
   memakai jalur biru #1976d2 (`.bg-primary`) dengan kelas `headline` 24px, jadi
   ia satu-satunya halaman yang kepalanya biru dalam sistem teal, dan tajuknya
   dua kali ganda lebih besar daripada tajuk kad di tempat lain. */
.kad__tajuk {
  border-bottom: 1px solid #dbeeeb;
}
.kad__tajuk .v-icon {
  color: #037769;
}
/* Menggantikan `color="primary"` — primary Vuetify ialah biru. Warna disebut
   secara literal dan bukan var(--teal-700) kerana pembolehubah itu ditakrif
   pada `.Menu` dalam MenuHeader, bukan pada :root. */
.btn-teal.v-btn {
  background: #037769 !important;
  color: #ffffff !important;
  letter-spacing: normal;
}
.btn-teal.v-btn:hover { background: #026254 !important; }
/* Butang lumpuh mesti kekal kelihatan lumpuh: tanpa ini `!important` di atas
   akan mengecat butang "Mulakan Sync"/"Simpan" teal penuh walaupun ia tidak
   boleh diklik. */
.btn-teal.v-btn--disabled {
  background: #cfd8dc !important;
  color: rgba(0, 0, 0, .38) !important;
}
.log-panel {
  background: #0e1117;
  color: #d4d4d4;
  font-family: 'Consolas', 'Monaco', monospace;
  font-size: var(--fs-xs);
  line-height: 1.5;
  padding: 10px 12px;
  height: 280px;
  overflow-y: auto;
  border-radius: 4px;
}
.log-line {
  white-space: pre-wrap;
  word-break: break-word;
}
.log-ts {
  color: #6b7280;
  margin-right: 8px;
}
.log-info .log-msg { color: #d4d4d4; }
.log-ok .log-msg { color: #22c55e; }
.log-warn .log-msg { color: #f59e0b; }
.log-error .log-msg { color: #ef4444; }
.log-done .log-msg { color: #38bdf8; font-weight: 600; }
.log-empty {
  color: #6b7280;
  text-align: center;
  padding: 24px 0;
}
.log-full {
  background: #0e1117;
  color: #d4d4d4;
  font-family: 'Consolas', 'Monaco', monospace;
  font-size: var(--fs-xs);
  padding: 12px;
  max-height: 400px;
  overflow: auto;
  border-radius: 4px;
  white-space: pre-wrap;
  word-break: break-word;
}
</style>
